import React from "react";
import { TEAM } from "@/lib/mockData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const roleColor = {
  Admin: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  Manager: "bg-purple-500/10 text-purple-300 border-purple-500/30",
  Viewer: "bg-zinc-500/10 text-zinc-300 border-zinc-700",
};

const Settings = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-7" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Workspace, team and notification preferences.</p>
      </div>

      {/* Agency profile */}
      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <h2 className="text-base font-semibold tracking-tight">Agency Profile</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-300">Agency name</Label>
            <Input defaultValue="Bagfluence HQ" className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100" data-testid="agency-name-input" />
          </div>
          <div>
            <Label className="text-zinc-300">Contact email</Label>
            <Input defaultValue="hello@bagfluence.com" className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100" />
          </div>
        </div>
        <Button className="mt-4 bg-blue-600 hover:bg-blue-500 text-white" onClick={() => toast.success("Profile updated")}>Save</Button>
      </section>

      {/* Team */}
      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight">Team</h2>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white">+ Invite member</Button>
        </div>
        <div className="mt-4 divide-y divide-white/5">
          {TEAM.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-3" data-testid={`team-member-${m.id}`}>
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-200 text-sm font-semibold">{m.name.charAt(0)}</span>
                <div>
                  <div className="text-sm text-zinc-100">{m.name}</div>
                  <div className="text-xs text-zinc-500">{m.email}</div>
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${roleColor[m.role]}`}>{m.role}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Notifications */}
      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <h2 className="text-base font-semibold tracking-tight">Notifications</h2>
        <div className="mt-4 space-y-3">
          {[
            { k: "New lead notifications", d: "Email me when a new brand lead is captured" },
            { k: "Deliverable updates", d: "Ping me when a creator submits new content" },
            { k: "Weekly digest", d: "Monday morning summary of campaigns and revenue" },
          ].map((n) => (
            <div key={n.k} className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm text-zinc-100">{n.k}</div>
                <div className="text-xs text-zinc-500">{n.d}</div>
              </div>
              <Switch defaultChecked />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
        <h2 className="text-base font-semibold tracking-tight">Signed in as</h2>
        <p className="mt-2 text-sm text-zinc-300">{user?.name} · {user?.email}</p>
      </section>
    </div>
  );
};

export default Settings;
